import { r as registerInstance, h, H as Host, g as getElement } from './index-8f7fb048.js';
import { t as tokens_module } from './tokens.module-03e95cfb.js';
import { g as getNamespacedTagFor } from './index-739b155e.js';

const marketButtonGroupCss = ":host,::slotted(*),*{box-sizing:border-box}:host([disabled]){cursor:not-allowed !important;}:host([disabled]) *,:host([disabled]) ::slotted(*){cursor:not-allowed !important;pointer-events:none !important;}:host{display:flex;flex-direction:row-reverse}::slotted(.market-button){margin-left:var(--button-group-spacing)}:host([alignment='left']){flex-direction:row;justify-content:flex-start}:host([alignment='left']) ::slotted(.market-button){margin:0 var(--button-group-spacing) 0 0}:host([alignment='right']){justify-content:flex-start}:host([alignment='split']) ::slotted(.market-button:first-of-type){margin-left:auto}:host([alignment='fill']) ::slotted(.market-button){flex:1}:host([alignment='fill']) ::slotted(.market-button:last-of-type){margin-left:0}:host([alignment='fill']) .market-button-dropdown{margin-right:var(--button-group-spacing)}:host([alignment='stack']){flex-direction:column}:host([alignment='stack']) ::slotted(.market-button){margin:0 0 var(--button-group-spacing) 0}:host([alignment='stack'].show-overflow){flex-direction:row-reverse}:host([alignment='stack'].show-overflow) ::slotted(.market-button){flex-grow:1;margin:0 0 0 var(--button-group-spacing)}";

const MarketButtonGroup = class {
  constructor(hostRef) {
    registerInstance(this, hostRef);
    // The width of the primary button within the group, needed to determine which buttons will show.
    // Required only for resizing fill mode.
    this.primaryButtonWidth = 0;
    // The width of the secondary button within the group, needed to determine which buttons will show.
    this.secondaryButtonWidth = 0;
    this.handleResizing = () => {
      var _a;
      const marketButtonTag = getNamespacedTagFor('market-button');
      const marketButtonDropdownTagName = getNamespacedTagFor('market-button-dropdown');
      // Since buttons can either be slotted into market-button-group or moved
      // (by moveButtonNodes()) to the internal <market-button-dropdown> popover,
      // we need to look in both places to grab all rendered button nodes
      const buttons = [
        // Buttons either slotted into the default slot OR slotted into the "visible-buttons" slot by moveButtonNodes()
        ...this.el.querySelectorAll(marketButtonTag),
        // Buttons that have been moved to the overflow popover by moveButtonNodes()
        ...(((_a = this.el.shadowRoot
          .querySelector(`${marketButtonDropdownTagName} [slot="content"]`)) === null || _a === void 0 ? void 0 : _a.querySelectorAll(marketButtonTag)) || []),
      ];
      const buttonGroupWidth = this.el.offsetWidth;
      // The overflow button (48px) and margin (16px) required for the overflow button, if it's required.
      let overflowButtonBuffer = tokens_module.BUTTON_GROUP_OVERFLOW_BUTTON_WIDTH + tokens_module.BUTTON_GROUP_SPACING;
      if (buttons.length === 1) {
        this.visibleButtons = buttons;
        return;
      }
      // Primary button variable that may need max-width updates to support text truncation in buttons.
      const innerPrimaryButton = buttons[0].shadowRoot.querySelector('button');
      if (this.alignment === 'stack') {
        // Stack alignment always shows buttons
        this.visibleButtons = buttons;
        this.overflowButtons = [];
        if (innerPrimaryButton) {
          // Max width doesn't affect stack alignments, set it here in case another alignment set a max width.
          innerPrimaryButton.style.maxWidth = 'inherit';
        }
      }
      else {
        // All other button alignments show based on space available.
        if (innerPrimaryButton) {
          // Update max width of primary button for overflow
          innerPrimaryButton.style.maxWidth = `${buttonGroupWidth - overflowButtonBuffer}px`;
        }
        if (buttons.length === 2) {
          // Overflow button not needed in measurements for 2 buttons.
          overflowButtonBuffer = 0;
        }
        // In fill alignment, check for space with initial primary button size.
        // If not, use current primary button size. This handles the edge case in fill alignment,
        // where the button length grows based on screen size.
        // This allows us to know if there is enough space for the secondary button.
        const firstButtonWidthToCheck = this.alignment === 'fill' ? this.primaryButtonWidth : buttons[0].offsetWidth;
        if (firstButtonWidthToCheck + this.secondaryButtonWidth + overflowButtonBuffer <= buttonGroupWidth) {
          // All buttons fit, including overflow button if required.
          this.visibleButtons = buttons.slice(0, 2);
          this.overflowButtons = buttons.slice(2);
        }
        else {
          // Only first button fits, hide the rest in the overflow.
          this.visibleButtons = buttons.slice(0, 1);
          this.overflowButtons = buttons.slice(1);
        }
      }
      this.hasButtonOverflow = this.overflowButtons.length > 0;
      // Hiding overflow buttons until moveButtonNodes() does its work
      this.overflowButtons.forEach((button) => {
        button.style.display = 'none';
      });
    };
    this.moveButtonNodes = () => {
      const marketButtonDropdownTagName = getNamespacedTagFor('market-button-dropdown');
      // Moving visible and overflow buttons to the correct place and styling them
      // We need to move the button nodes rather than clone them because cloning
      // copies their attributes and values, but not all event listeners:
      // https://developer.mozilla.org/en-US/docs/Web/API/Node/cloneNode
      // (Note that a node can only exist in one place in the DOM at a time.)
      // Visible buttons get moved to this.el and slotted into the "visible-buttons"
      // slot. The reason this named slot exists is because we can't programmatically
      // move things back to the default slot and it makes sense to keep these nodes
      // in the light DOM where they started.
      this.visibleButtons.forEach((button) => {
        button.style.display = 'block';
        button.style.marginTop = '0';
        button.setAttribute('slot', 'visible-buttons');
        this.el.appendChild(button);
      });
      // If there are overflow buttons, they get moved into `<market-button-dropdown>`'s
      // internal `<market-popover>` via its "content" slot.
      if (this.hasButtonOverflow) {
        const overflowNode = this.el.shadowRoot.querySelector(`${marketButtonDropdownTagName} [slot="content"]`);
        this.overflowButtons.forEach((button, index) => {
          button.style.display = 'block';
          button.style.marginTop = index ? `${tokens_module.BUTTON_GROUP_SPACING}px` : '0';
          overflowNode.appendChild(button);
        });
      }
    };
    this.alignment = 'right';
    this.visibleButtons = [];
    this.overflowButtons = [];
    this.hasButtonOverflow = false;
  }
  componentDidLoad() {
    const marketButtonTag = getNamespacedTagFor('market-button');
    const buttons = [...this.el.querySelectorAll(marketButtonTag)];
    if (buttons.length === 0) {
      return;
    }
    // Margin (16px) required as space between buttons.
    const buttonMarginBuffer = tokens_module.BUTTON_GROUP_SPACING;
    // The minimum width for the primary button and the secondary button to show are determined by the length of the content within the buttons,
    // their padding, and the margin between buttons, if both are showing.
    let primaryButtonContentLength = 0;
    // The content of the buttons are all the possible spans within a button, such as icons, text, and carets.
    // This relies on the content of the buttons being wrapped by spans.
    buttons[0].shadowRoot
      .querySelectorAll('span')
      .forEach((elem) => (primaryButtonContentLength += elem.offsetWidth));
    let buttonPadding;
    switch (buttons[0].size) {
      case 'large':
        buttonPadding = tokens_module.BUTTON_LARGE_SIZE_PRIMARY_RANK_HORIZONTAL_PADDING;
        break;
      case 'small':
        buttonPadding = tokens_module.BUTTON_SMALL_SIZE_PRIMARY_RANK_HORIZONTAL_PADDING;
        break;
      case 'medium':
      default:
        buttonPadding = tokens_module.BUTTON_MEDIUM_SIZE_PRIMARY_RANK_HORIZONTAL_PADDING;
    }
    this.primaryButtonWidth = primaryButtonContentLength + buttonPadding * 2;
    if (buttons.length > 1) {
      let secondaryButtonContentLength = 0;
      buttons[1].shadowRoot
        .querySelectorAll('span')
        .forEach((elem) => (secondaryButtonContentLength += elem.offsetWidth));
      this.secondaryButtonWidth = secondaryButtonContentLength + buttonPadding + buttonMarginBuffer;
    }
    // Set up primary button styling. This is required to turn on text truncation in buttons.
    // Since button group cannot style shadow DOMs in other elements, we have to programmatically add it here.
    const innerPrimaryButton = buttons[0].shadowRoot.querySelector('button');
    innerPrimaryButton.style.whiteSpace = 'nowrap';
    innerPrimaryButton.style.overflow = 'hidden';
    innerPrimaryButton.style.textOverflow = 'ellipsis';
    // This is ignored because ResizeObserver isn't identifiable to TypeScript.
    // This is the quickest solution to have it work without importing a polyfill/types package.
    // @ts-ignore
    const resizer = new ResizeObserver(this.handleResizing);
    resizer.observe(this.el);
    this.handleResizing();
  }
  // On alignment change, recalculate visible buttons. This is mostly for ease of use in Storybook.
  alignmentWatcher() {
    this.handleResizing();
  }
  componentDidUpdate() {
    // Using componentDidUpdate ensures that the component waits to move its button
    // nodes until handleResizing() has determined the value of this.hasButtonsOverflow,
    // which dictates whether or not a `<market-button-dropdown>` is rendered
    // into the the component shadow DOM (can't move stuff to a part of the DOM
    // that doesn't exist yet!)
    this.moveButtonNodes();
  }
  render() {
    const MarketButtonDropdownTagName = getNamespacedTagFor('market-button-dropdown');
    const MarketButtonTagName = getNamespacedTagFor('market-button');
    return (h(Host, { class: `market-button-group ${this.hasButtonOverflow ? 'show-overflow' : ''}` }, h("slot", null), h("slot", { name: "visible-buttons" }), Boolean(this.hasButtonOverflow) && (h(MarketButtonDropdownTagName, { "no-caret": true }, h(MarketButtonTagName, { slot: "trigger" }, h("svg", { slot: "icon", width: "16", height: "16", viewBox: "0 0 16 16", fill: "none", xmlns: "http://www.w3.org/2000/svg" }, h("path", { "fill-rule": "evenodd", "clip-rule": "evenodd", d: "M4 8C4 9.10457 3.10457 10 2 10C0.895431 10 0 9.10457 0 8C0 6.89543 0.895431 6 2 6C3.10457 6 4 6.89543 4 8ZM10 8C10 9.10457 9.10457 10 8 10C6.89543 10 6 9.10457 6 8C6 6.89543 6.89543 6 8 6C9.10457 6 10 6.89543 10 8ZM14 10C15.1046 10 16 9.10457 16 8C16 6.89543 15.1046 6 14 6C12.8954 6 12 6.89543 12 8C12 9.10457 12.8954 10 14 10Z", fill: "black", "fill-opacity": "0.9" }))), h("div", { slot: "content" })))));
  }
  get el() { return getElement(this); }
  static get watchers() { return {
    "alignment": ["alignmentWatcher"]
  }; }
};
MarketButtonGroup.style = marketButtonGroupCss;

export { MarketButtonGroup as market_button_group };
